import { Box, Container, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import LoadingSpinner from '@/components/LoadingSpinner'
import { useAppSelector } from '@/hooks/redux'
import { useGetCaseDetailsQuery } from '@/store/apiService'
import {
  removeAllImages,
  setCanvasImage,
  setImageUploadFailed,
} from '@/store/appSlice'

import ProjectDetailHeader from './ProjectDetailHeader'
import ProjectImageGallery from './ProjectImageGallery'

function ProjectDetail() {
  const dispatch = useDispatch()
  const params = useParams()
  const { id } = params

  const [poll, setPoll] = useState(false)
  const lastImageCountRef = useRef<number | null>(null)
  const [ordering, setOrdering] = useState<string>('-created_ts')
  const [progressArray, setProgressArray] = useState<number[]>([])
  const [uploadingCount, setUploadingCount] = useState(0)

  const isImageUploadFailed = useAppSelector(
    (state) => state.app.isImageUploadFailed,
  )

  const pendingUploadsRef = useRef(0)

  const {
    data: caseDetails,
    refetch,
    isLoading,
  } = useGetCaseDetailsQuery(
    {
      caseId: id,
      ordering,
    },
    {
      skip: !id,
      pollingInterval: poll ? 2000 : 0,
    },
  )

  useEffect(() => {
    if (id) {
      refetch()
    }
  }, [id, refetch])

  useEffect(() => {
    if (isImageUploadFailed) {
      setPoll(false)
      setUploadingCount(0)
      setProgressArray([])
      pendingUploadsRef.current = 0
    }
  }, [isImageUploadFailed])

  useEffect(() => {
    if (poll && caseDetails?.images) {
      const currentCount = caseDetails.images.length
      const uploaded = lastImageCountRef.current ?? 0
      const newUploads = currentCount - uploaded

      if (newUploads > 0) {
        setUploadingCount((prev) => {
          const remaining = Math.max(prev - newUploads, 0)
          if (remaining === 0) {
            setPoll(false)
            setProgressArray([])
            toast.success('Upload successful!')
          }
          return remaining
        })
        lastImageCountRef.current = currentCount
      }
    }
  }, [caseDetails, poll])

  useEffect(() => {
    dispatch(removeAllImages())
    dispatch(setCanvasImage(null))
  }, [dispatch])

  const handleUploadInitiated = (numFiles: number) => {
    const currentLength = progressArray.length
    setUploadingCount((prev) => prev + numFiles)
    setProgressArray((prev) => [...prev, ...Array(numFiles).fill(0)])
    dispatch(setImageUploadFailed(false))
    return currentLength
  }

  const updateProgress = (index: number, value: number) => {
    setProgressArray((prev) => {
      const newArray = [...prev]
      newArray[index] = value
      return newArray
    })
  }

  const handleUploadStart = (numFiles: number) => {
    pendingUploadsRef.current += numFiles
    lastImageCountRef.current = caseDetails?.images?.length ?? 0
    setPoll(true)
  }

  if (!id) return <Typography>Project not found.</Typography>
  return (
    <Box overflow="auto" flex={1} sx={{ my: '32px' }}>
      <Container maxWidth="lg">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="800px"
          >
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            <ProjectDetailHeader
              refetch={refetch}
              caseDetails={caseDetails}
              onUploadInitiated={handleUploadInitiated}
              onUploadStart={handleUploadStart}
              updateProgress={updateProgress}
            />
            <ProjectImageGallery
              caseDetails={caseDetails || []}
              setOrdering={setOrdering}
              ordering={ordering}
              refetch={refetch}
              onUploadInitiated={handleUploadInitiated}
              onUploadStart={handleUploadStart}
              uploadingCount={uploadingCount}
              progressArray={progressArray}
              updateProgress={updateProgress}
            />
          </>
        )}
      </Container>
    </Box>
  )
}

export default ProjectDetail
